import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Sparkles, Copy, Mail, MessageCircle, Trash2, Pencil, Target, ExternalLink, Send, FileWarning, Settings as SettingsIcon, Plus as PlusIcon, Search, ArrowUpDown } from "lucide-react";
import { statusLabel, statusColor, fmtEur2, fmtDate, fmtEur } from "@/lib/format";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { openWa, waNumberWarning } from "@/lib/whatsapp";
import { API } from "@/lib/api";

const emptyItem = () => ({ description: "", quantity: 1, unit_price: 0, iva_pct: null });
const empty = {
  client_id: "",
  project_id: "",
  number: "",
  items: [emptyItem()],
  issued_date: "",
  due_date: "",
  status: "inviata",
  notes: "",
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [goals, setGoals] = useState([]);
  const [profile, setProfile] = useState({ sdi_regime_fiscale: "RF01", default_iva_pct: 22 });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_desc");
  const navigate = useNavigate();
  // WA send-via-Business feature retired — the follow-up modal now uses
  // wa.me click-to-chat via the WhatsApp helper.

  // New-invoice dialog
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(empty);

  // Edit-invoice dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [saving, setSaving] = useState(false);

  // Follow-up dialog
  const [fuOpen, setFuOpen] = useState(false);
  const [fuInvoice, setFuInvoice] = useState(null);
  const [fuChannel, setFuChannel] = useState("email");
  const [fuTone, setFuTone] = useState("cortese");
  const [fuMessage, setFuMessage] = useState("");
  const [fuLoading, setFuLoading] = useState(false);

  // SdI readiness dialog (shown when required fiscal fields are missing)
  const [sdiWarnOpen, setSdiWarnOpen] = useState(false);
  const [sdiMissing, setSdiMissing] = useState([]);
  const [sdiMissingKind, setSdiMissingKind] = useState("issuer"); // "issuer" | "client"
  const [sdiMissingClientId, setSdiMissingClientId] = useState(null);
  const [sdiMissingClientName, setSdiMissingClientName] = useState("");
  const [sdiDownloading, setSdiDownloading] = useState(null);

  const load = async () => {
    const [i, c, p, g, prof] = await Promise.all([
      api.get("/invoices"),
      api.get("/clients"),
      api.get("/projects"),
      api.get("/goals"),
      api.get("/profile").catch(() => ({ data: null })),
    ]);
    setInvoices(i.data); setClients(c.data); setProjects(p.data); setGoals(g.data);
    if (prof.data) {
      setProfile({
        sdi_regime_fiscale: prof.data.sdi_regime_fiscale || "RF01",
        default_iva_pct: prof.data.default_iva_pct ?? 22,
      });
    }
    try {
      const { data } = await api.get("/whatsapp/account");
      // no-op: legacy check kept only if a future WA Business flow is re-enabled
      void data;
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => {
    const c = clients.find((x) => x.client_id === id);
    if (!c) return null;
    return c.company || c.name || null;
  };

  // ---- Filters + sort ----
  const clientsForSelect = useMemo(
    () => [...clients].sort((a, b) => (a.company || a.name || "").localeCompare(b.company || b.name || "", "it", { sensitivity: "base" })),
    [clients],
  );
  const clientsWithInvoices = useMemo(() => {
    const ids = new Set(invoices.map((i) => i.client_id));
    return clientsForSelect.filter((c) => ids.has(c.client_id));
  }, [clientsForSelect, invoices]);

  const visibleInvoices = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = invoices.filter((inv) => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (clientFilter !== "all" && inv.client_id !== clientFilter) return false;
      if (!term) return true;
      const hay = `${inv.number || ""} ${inv.notes || ""} ${clientName(inv.client_id) || ""}`.toLowerCase();
      return hay.includes(term);
    });
    const ts = (i) => new Date(i.updated_at || i.issued_date || i.created_at || 0).getTime();
    const cmp = {
      updated_desc: (a, b) => ts(b) - ts(a),
      updated_asc: (a, b) => ts(a) - ts(b),
      alpha_asc: (a, b) => (a.number || "").localeCompare(b.number || "", "it", { sensitivity: "base", numeric: true }),
    }[sortBy] || (() => 0);
    return [...list].sort(cmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, statusFilter, clientFilter, q, sortBy, clients]);

  const openNew = () => {
    const today = new Date().toISOString().slice(0,10);
    const due = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0,10);
    const nextNum = `2026-${String(invoices.length + 1).padStart(3,"0")}`;
    setForm({
      ...empty,
      items: [emptyItem()],
      client_id: clients[0]?.client_id || "",
      number: nextNum,
      issued_date: today,
      due_date: due,
    });
    setNewOpen(true);
  };

  // ---- Line items helpers ----
  const isForfettario = profile.sdi_regime_fiscale === "RF19";
  const defaultIva = Number(profile.default_iva_pct ?? 22);

  const updateItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
  }));

  // Live totals — mirror the backend logic in server.py create_invoice
  const totals = useMemo(() => {
    let imponibile = 0;
    let iva = 0;
    for (const it of form.items || []) {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unit_price) || 0;
      const line = Math.round(qty * price * 100) / 100;
      const ivaPct = isForfettario ? 0 : (it.iva_pct == null ? defaultIva : Number(it.iva_pct));
      imponibile += line;
      iva += Math.round(line * ivaPct) / 100;
    }
    imponibile = Math.round(imponibile * 100) / 100;
    iva = Math.round(iva * 100) / 100;
    const stampDuty = isForfettario && imponibile > 77.47 ? 2 : 0;
    const totale = Math.round((imponibile + iva + stampDuty) * 100) / 100;
    return { imponibile, iva, stampDuty, totale };
  }, [form.items, isForfettario, defaultIva]);

  const save = async () => {
    if (!form.client_id) return toast.error("Seleziona un cliente");
    if (!form.number.trim()) return toast.error("Inserisci il numero fattura");
    const cleanItems = (form.items || []).filter(
      (it) => (it.description || "").trim() && Number(it.quantity) > 0 && Number(it.unit_price) >= 0,
    );
    if (cleanItems.length === 0) {
      return toast.error("Aggiungi almeno una riga con descrizione, quantità e prezzo");
    }
    try {
      const payload = {
        client_id: form.client_id,
        project_id: form.project_id || null,
        number: form.number,
        issued_date: form.issued_date,
        due_date: form.due_date,
        status: form.status,
        notes: form.notes || null,
        items: cleanItems.map((it) => ({
          description: it.description.trim(),
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          iva_pct: isForfettario ? 0 : (it.iva_pct == null ? defaultIva : Number(it.iva_pct)),
          natura: isForfettario ? "N2.2" : (it.natura || null),
        })),
      };
      await api.post("/invoices", payload);
      toast.success("Fattura creata"); setNewOpen(false); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const openEdit = (inv) => {
    setEditInvoice({
      ...inv,
      issued_date: inv.issued_date ? inv.issued_date.slice(0,10) : "",
      due_date: inv.due_date ? inv.due_date.slice(0,10) : "",
      paid_date: inv.paid_date ? inv.paid_date.slice(0,10) : "",
      notes: inv.notes || "",
      project_id: inv.project_id || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editInvoice) return;
    if (!editInvoice.number?.trim()) return toast.error("Il numero è obbligatorio");
    if (!editInvoice.client_id) return toast.error("Seleziona un cliente");
    setSaving(true);
    try {
      const payload = {
        number: editInvoice.number,
        client_id: editInvoice.client_id,
        project_id: editInvoice.project_id || null,
        amount: parseFloat(editInvoice.amount) || 0,
        issued_date: editInvoice.issued_date,
        due_date: editInvoice.due_date,
        status: editInvoice.status,
        paid_date: editInvoice.paid_date || null,
        notes: editInvoice.notes || null,
      };
      await api.put(`/invoices/${editInvoice.invoice_id}`, payload);
      toast.success("Fattura aggiornata");
      setEditOpen(false); setEditInvoice(null); load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async (inv, event) => {
    event?.stopPropagation?.();
    await api.put(`/invoices/${inv.invoice_id}`, { status: "pagata" });
    toast.success("Fattura segnata come pagata"); load();
  };

  const remove = async (id, event) => {
    event?.stopPropagation?.();
    if (!window.confirm("Eliminare la fattura?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Fattura eliminata");
    load();
  };

  const openFollowUp = (inv, event) => {
    event?.stopPropagation?.();
    setFuInvoice(inv); setFuMessage(""); setFuChannel("email"); setFuTone("cortese"); setFuOpen(true);
  };

  // ---- SdI XML: click-safe download. We rely on the backend as source of truth
  // for readiness (GET returns 422 with structured detail when the fiscal profile
  // is incomplete). No duplicated client-side rules.
  const openSdiXml = async (inv, event) => {
    event?.stopPropagation?.();
    setSdiDownloading(inv.invoice_id);
    try {
      const res = await fetch(`${API}/invoices/${inv.invoice_id}/sdi-xml`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* ignore */ }
        if (detail && detail.error === "profilo_incompleto") {
          setSdiMissing(detail.missing || ["Dati aziendali incompleti"]);
          setSdiMissingKind("issuer");
          setSdiMissingClientId(null);
          setSdiMissingClientName("");
          setSdiWarnOpen(true);
        } else if (detail && detail.error === "cliente_incompleto") {
          setSdiMissing(detail.missing || ["Dati fiscali del cliente incompleti"]);
          setSdiMissingKind("client");
          setSdiMissingClientId(detail.client_id || null);
          setSdiMissingClientName(detail.client_name || "");
          setSdiWarnOpen(true);
        } else if (typeof detail === "string") {
          toast.error(detail);
        } else if (detail?.message) {
          toast.error(detail.message);
        } else {
          toast.error(`Errore generazione XML (${res.status})`);
        }
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `fattura_${inv.number || inv.invoice_id}.xml`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("XML SdI scaricato");
    } catch (e) {
      toast.error("Errore di rete durante il download XML");
    } finally {
      setSdiDownloading(null);
    }
  };

  const goToSettings = () => {
    setSdiWarnOpen(false);
    navigate("/impostazioni");
  };

  const goToClient = () => {
    setSdiWarnOpen(false);
    navigate("/clienti");
  };

  const generateFu = async () => {
    setFuLoading(true);
    try {
      const { data } = await api.post("/followups/generate", {
        invoice_id: fuInvoice.invoice_id, channel: fuChannel, tone: fuTone,
      });
      setFuMessage(data.message);
    } catch (e) {
      console.error("Follow-up generation failed", e);
      toast.error("Errore AI, riprova");
    } finally { setFuLoading(false); }
  };

  const copyFu = async () => {
    if (!fuMessage) return;
    try {
      await navigator.clipboard.writeText(fuMessage);
      toast.success("Messaggio copiato negli appunti");
      // Track as generated/copied so it appears in follow-up history
      try {
        await api.post("/followups/send", {
          invoice_id: fuInvoice.invoice_id, channel: fuChannel, message: fuMessage,
        });
      } catch { /* silent */ }
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const openInMail = () => {
    if (!fuInvoice || !fuMessage) return;
    const clientDoc = clients.find(c => c.client_id === fuInvoice.client_id);
    const to = clientDoc?.email || "";
    // First line often starts with "Oggetto: ..."; extract if present
    let subject = `Sollecito pagamento — Fattura ${fuInvoice.number}`;
    let body = fuMessage;
    const m = fuMessage.match(/^\s*Oggetto:\s*(.+?)\r?\n([\s\S]*)$/i);
    if (m) { subject = m[1].trim(); body = m[2].trim(); }
    const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const openInWhatsApp = () => {
    if (!fuInvoice || !fuMessage) return;
    const clientDoc = clients.find(c => c.client_id === fuInvoice.client_id);
    const raw = clientDoc?.whatsapp || clientDoc?.phone || "";
    const ok = openWa(raw, fuMessage);
    if (!ok) {
      toast.error(waNumberWarning(raw) || "Il cliente non ha un numero WhatsApp valido in formato internazionale (+39…).");
    } else {
      setFuOpen(false);
    }
  };

  const overdue = invoices.filter(i => i.status === "scaduta");
  const pending = invoices.filter(i => i.status === "inviata");
  const paid = invoices.filter(i => i.status === "pagata");

  // ---- Revenue vs monthly goal ----
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthName = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" }).format(now);
  const monthRevenue = paid
    .filter(i => (i.paid_date || "").slice(0, 7) === monthKey)
    .reduce((sum, i) => sum + (i.amount || 0), 0);
  const monthlyGoal = goals.find(g => g.metric === "ricavi" && g.period === "mensile");
  const targetAmount = monthlyGoal?.target || 0;
  const pct = targetAmount > 0 ? Math.min(100, Math.round((monthRevenue / targetAmount) * 100)) : 0;
  const missing = Math.max(0, targetAmount - monthRevenue);
  const barTone = pct >= 100 ? "bg-zen-primary" : pct >= 60 ? "bg-zen-primary/80" : pct >= 30 ? "bg-zen-info" : "bg-zen-alert";

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Fatturazione</span>
          <h1 className="mt-2 font-heading text-3xl">Fatture & Solleciti</h1>
          <p className="text-zen-ink2 mt-1">{overdue.length} scadute · {pending.length} in attesa · {paid.length} pagate</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zen-ink2"/>
            <Input
              data-testid="search-invoices"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Cerca…"
              className="pl-9 w-56"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-invoice-status" className="w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="bozza">Bozza</SelectItem>
              <SelectItem value="inviata">Inviate</SelectItem>
              <SelectItem value="pagata">Pagate</SelectItem>
              <SelectItem value="scaduta">Scadute</SelectItem>
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="filter-invoice-client" className="w-48"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {clientsWithInvoices.map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name || "—"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="sort-invoices" className="w-56">
              <ArrowUpDown size={14} className="text-zen-ink2 mr-1"/>
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_desc">Più recenti</SelectItem>
              <SelectItem value="updated_asc">Meno recenti</SelectItem>
              <SelectItem value="alpha_asc">Numero fattura</SelectItem>
            </SelectContent>
          </Select>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-btn" onClick={openNew} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
              <Plus size={16} className="mr-1"/>Nuova fattura
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuova fattura</DialogTitle>
              <DialogDescription className="sr-only">Inserisci i dettagli della fattura, aggiungi le righe e salva.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Numero *</Label><Input data-testid="if-number" value={form.number} onChange={(e)=>setForm({...form, number:e.target.value})}/></div>
                <div><Label>Cliente (azienda) *</Label>
                  <Select value={form.client_id} onValueChange={(v)=>setForm({...form, client_id:v})}>
                    <SelectTrigger data-testid="if-client"><SelectValue placeholder="Seleziona un'azienda…"/></SelectTrigger>
                    <SelectContent>{clients.map(c=><SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name || "—"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Progetto</Label>
                <Select value={form.project_id||"_none"} onValueChange={(v)=>setForm({...form, project_id: v==="_none"?"":v})}>
                  <SelectTrigger><SelectValue placeholder="Nessuno"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nessuno</SelectItem>
                    {projects.filter(p=>!form.client_id || p.client_id===form.client_id).map(p=><SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Righe fattura */}
              <div className="space-y-2" data-testid="if-items">
                <div className="flex items-center justify-between">
                  <Label>Righe fattura *</Label>
                  {isForfettario && (
                    <span className="text-xs text-zen-info bg-zen-info/10 border border-zen-info/30 rounded-full px-2 py-0.5">
                      Regime forfettario · IVA 0% + natura N2.2
                    </span>
                  )}
                </div>
                <div className="rounded-xl border border-zen-border overflow-hidden">
                  <div className="hidden md:grid grid-cols-[minmax(0,1fr)_80px_100px_90px_100px_36px] gap-2 items-center px-3 py-2 bg-zen-bg text-xs text-zen-ink2 uppercase tracking-wide">
                    <div>Descrizione</div>
                    <div className="text-right">Qty</div>
                    <div className="text-right">Prezzo €</div>
                    <div className="text-right">IVA %</div>
                    <div className="text-right">Totale</div>
                    <div/>
                  </div>
                  {form.items.map((it, idx) => {
                    const qty = Number(it.quantity) || 0;
                    const price = Number(it.unit_price) || 0;
                    const line = Math.round(qty * price * 100) / 100;
                    const ivaShown = isForfettario ? 0 : (it.iva_pct == null ? defaultIva : Number(it.iva_pct));
                    return (
                      <div
                        key={idx}
                        data-testid={`if-item-${idx}`}
                        className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_80px_100px_90px_100px_36px] gap-2 items-center px-3 py-2 border-t border-zen-border first:border-t-0"
                      >
                        <Input
                          data-testid={`if-item-${idx}-desc`}
                          placeholder="Es. Sviluppo landing page"
                          value={it.description}
                          onChange={(e)=>updateItem(idx, { description: e.target.value })}
                        />
                        <Input
                          data-testid={`if-item-${idx}-qty`}
                          type="number" step="0.01" min="0" className="md:text-right"
                          value={it.quantity}
                          onChange={(e)=>updateItem(idx, { quantity: e.target.value === "" ? "" : parseFloat(e.target.value) || 0 })}
                        />
                        <Input
                          data-testid={`if-item-${idx}-price`}
                          type="number" step="0.01" min="0" className="md:text-right"
                          value={it.unit_price}
                          onChange={(e)=>updateItem(idx, { unit_price: e.target.value === "" ? "" : parseFloat(e.target.value) || 0 })}
                        />
                        <Input
                          data-testid={`if-item-${idx}-iva`}
                          type="number" step="1" min="0" max="30" className="md:text-right"
                          disabled={isForfettario}
                          value={isForfettario ? 0 : (it.iva_pct == null ? defaultIva : it.iva_pct)}
                          onChange={(e)=>updateItem(idx, { iva_pct: e.target.value === "" ? null : parseFloat(e.target.value) })}
                        />
                        <div className="text-right font-medium text-zen-ink text-sm tabular-nums" data-testid={`if-item-${idx}-total`}>
                          {fmtEur2(line + Math.round(line * ivaShown) / 100)}
                        </div>
                        <button
                          type="button"
                          data-testid={`if-item-${idx}-remove`}
                          onClick={()=>removeItem(idx)}
                          disabled={form.items.length <= 1}
                          className="text-zen-alert hover:opacity-70 disabled:opacity-30 disabled:cursor-not-allowed self-center justify-self-end"
                          title={form.items.length <= 1 ? "Almeno una riga obbligatoria" : "Rimuovi riga"}
                        >
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <Button
                  type="button" variant="outline" size="sm"
                  data-testid="if-item-add"
                  onClick={addItem}
                  className="rounded-full"
                >
                  <PlusIcon size={13} className="mr-1"/> Aggiungi riga
                </Button>
              </div>

              {/* Totali live */}
              <div className="rounded-xl bg-zen-bg p-3 space-y-1.5 text-sm" data-testid="if-totals">
                <div className="flex justify-between text-zen-ink2">
                  <span>Imponibile</span>
                  <span className="tabular-nums" data-testid="if-imponibile">{fmtEur2(totals.imponibile)}</span>
                </div>
                <div className="flex justify-between text-zen-ink2">
                  <span>IVA {isForfettario ? "(0%)" : ""}</span>
                  <span className="tabular-nums" data-testid="if-iva">{fmtEur2(totals.iva)}</span>
                </div>
                {totals.stampDuty > 0 && (
                  <div className="flex justify-between text-zen-ink2">
                    <span>Marca da bollo virtuale</span>
                    <span className="tabular-nums" data-testid="if-bollo">{fmtEur2(totals.stampDuty)}</span>
                  </div>
                )}
                <div className="flex justify-between font-heading text-lg text-zen-ink border-t border-zen-border pt-2 mt-2">
                  <span>Totale</span>
                  <span className="tabular-nums text-zen-primary" data-testid="if-total">{fmtEur2(totals.totale)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Emessa il</Label><Input type="date" value={form.issued_date} onChange={(e)=>setForm({...form, issued_date:e.target.value})}/></div>
                <div><Label>Scadenza</Label><Input type="date" value={form.due_date} onChange={(e)=>setForm({...form, due_date:e.target.value})}/></div>
              </div>
              <div><Label>Note</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setNewOpen(false)}>Annulla</Button>
              <Button data-testid="if-save" onClick={save} className="bg-zen-primary hover:bg-zen-primaryHover">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Revenue vs monthly goal counter */}
      <div className="zen-card p-5 md:p-6" data-testid="revenue-goal-card">
        {targetAmount > 0 ? (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-zen-primary/10 text-zen-primary flex items-center justify-center">
                  <Target size={18} strokeWidth={1.5}/>
                </div>
                <div>
                  <div className="zen-label">Ricavi vs obiettivo · <span className="capitalize">{monthName}</span></div>
                  <div className="mt-0.5 text-zen-ink">
                    <span data-testid="revenue-current" className="font-heading text-2xl">{fmtEur2(monthRevenue)}</span>
                    <span className="text-zen-ink2 mx-2">/</span>
                    <span data-testid="revenue-target" className="font-heading text-2xl">{fmtEur(targetAmount)}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl text-zen-primary" data-testid="revenue-pct">{pct}%</div>
                <div className="text-xs text-zen-ink2 mt-0.5">
                  {pct >= 100 ? "🎉 Obiettivo raggiunto!" : `${fmtEur(missing)} al traguardo`}
                </div>
              </div>
            </div>
            <div className="mt-4 h-2.5 bg-zen-border rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${barTone}`}
                style={{ width: `${pct}%` }}
                data-testid="revenue-progress-bar"
              />
            </div>
          </>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-xl bg-zen-subtle/30 text-zen-ink2 flex items-center justify-center shrink-0">
              <Target size={18} strokeWidth={1.5}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-zen-ink font-medium">Nessun obiettivo di ricavi mensili impostato.</div>
              <div className="text-sm text-zen-ink2">Impostane uno per tracciare i tuoi progressi mese per mese.</div>
            </div>
            <Link to="/obiettivi" data-testid="goto-goals-link" className="text-sm text-zen-primary hover:underline whitespace-nowrap">
              Imposta obiettivo →
            </Link>
          </div>
        )}
      </div>

      <div className="zen-card overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-zen-bg text-zen-ink2">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Numero</th>
              <th className="text-left px-5 py-3 font-medium">Cliente</th>
              <th className="text-right px-5 py-3 font-medium">Importo</th>
              <th className="text-left px-5 py-3 font-medium">Scadenza</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="text-right px-5 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zen-border">
            {invoices.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-zen-ink2">Nessuna fattura ancora.</td></tr>
            ) : visibleInvoices.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-zen-ink2">Nessuna fattura corrisponde ai filtri attuali.</td></tr>
            ) : visibleInvoices.map(i => (
              <tr
                key={i.invoice_id}
                data-testid={`invoice-${i.invoice_id}`}
                onClick={()=>openEdit(i)}
                className="hover:bg-zen-bg/50 cursor-pointer transition-colors"
                title="Clicca per modificare"
              >
                <td className="px-5 py-4 font-medium text-zen-ink flex items-center gap-2">
                  <Pencil size={12} className="text-zen-ink2 opacity-60"/>
                  {i.number}
                </td>
                <td className="px-5 py-4">{clientName(i.client_id) || "—"}</td>
                <td className="px-5 py-4 text-right font-medium">{fmtEur2(i.amount)}</td>
                <td className="px-5 py-4 text-zen-ink2">{fmtDate(i.due_date)}</td>
                <td className="px-5 py-4"><Badge className={`${statusColor[i.status]} rounded-full font-normal`}>{statusLabel[i.status]}</Badge></td>
                <td className="px-5 py-4">
                  <div className="flex gap-2 justify-end items-center">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${i.invoice_id}/pdf`}
                      target="_blank" rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`pdf-${i.invoice_id}`}
                      title="Scarica PDF"
                      className="text-zen-ink2 hover:text-zen-primary text-xs px-2 py-1 rounded-full border border-zen-border hover:border-zen-primary/30"
                    >PDF</a>
                    <button
                      type="button"
                      onClick={(e) => openSdiXml(i, e)}
                      disabled={sdiDownloading === i.invoice_id}
                      data-testid={`sdi-${i.invoice_id}`}
                      title="Scarica FatturaPA XML per SdI"
                      className="text-zen-primary hover:text-zen-primaryHover text-xs px-2 py-1 rounded-full border border-zen-primary/30 bg-zen-primary/5 disabled:opacity-60"
                    >{sdiDownloading === i.invoice_id ? "…" : "SdI"}</button>
                    {(i.status === "scaduta" || i.status === "inviata") && (
                      <>
                        <Button data-testid={`followup-${i.invoice_id}`} size="sm" variant="outline" onClick={(e)=>openFollowUp(i, e)} className="rounded-full">
                          <Sparkles size={13} className="mr-1"/> Sollecito AI
                        </Button>
                        <Button data-testid={`paid-${i.invoice_id}`} size="sm" onClick={(e)=>markPaid(i, e)} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
                          Segna pagata
                        </Button>
                      </>
                    )}
                    <button data-testid={`del-invoice-${i.invoice_id}`} onClick={(e)=>remove(i.invoice_id, e)} className="text-zen-alert hover:opacity-70"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="invoices-mobile-list">
        {invoices.length === 0 ? (
          <div className="zen-card p-8 text-center text-zen-ink2">Nessuna fattura ancora.</div>
        ) : visibleInvoices.length === 0 ? (
          <div className="zen-card p-8 text-center text-zen-ink2">Nessuna fattura corrisponde ai filtri attuali.</div>
        ) : visibleInvoices.map((i) => (
          <div
            key={i.invoice_id}
            data-testid={`invoice-m-${i.invoice_id}`}
            className="zen-card p-4 space-y-3"
            onClick={() => openEdit(i)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-zen-ink2">{i.number}</div>
                <div className="font-medium text-zen-ink truncate">{clientName(i.client_id) || "—"}</div>
              </div>
              <Badge className={`${statusColor[i.status]} rounded-full font-normal shrink-0`}>{statusLabel[i.status]}</Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zen-ink2">Scad. {fmtDate(i.due_date)}</span>
              <span className="font-medium text-zen-ink">{fmtEur2(i.amount)}</span>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-zen-border" onClick={(e) => e.stopPropagation()}>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${i.invoice_id}/pdf`}
                target="_blank" rel="noreferrer"
                data-testid={`m-pdf-${i.invoice_id}`}
                className="inline-flex items-center text-zen-ink2 hover:text-zen-primary text-xs px-3 py-1.5 rounded-full border border-zen-border hover:border-zen-primary/30"
              >PDF</a>
              <button
                type="button"
                onClick={(e) => openSdiXml(i, e)}
                disabled={sdiDownloading === i.invoice_id}
                data-testid={`m-sdi-${i.invoice_id}`}
                className="text-zen-primary hover:text-zen-primaryHover text-xs px-3 py-1.5 rounded-full border border-zen-primary/30 bg-zen-primary/5 disabled:opacity-60"
              >{sdiDownloading === i.invoice_id ? "…" : "SdI"}</button>
              {(i.status === "scaduta" || i.status === "inviata") && (
                <>
                  <Button data-testid={`m-followup-${i.invoice_id}`} size="sm" variant="outline" onClick={(e)=>openFollowUp(i, e)} className="rounded-full">
                    <Sparkles size={13} className="mr-1"/> Sollecito
                  </Button>
                  <Button data-testid={`m-paid-${i.invoice_id}`} size="sm" onClick={(e)=>markPaid(i, e)} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
                    Pagata
                  </Button>
                </>
              )}
              <button data-testid={`m-del-invoice-${i.invoice_id}`} onClick={(e)=>remove(i.invoice_id, e)} className="ml-auto text-zen-alert hover:opacity-70 p-2">
                <Trash2 size={14}/>
              </button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Modifica fattura</DialogTitle>
            <DialogDescription className="sr-only">Modifica i campi della fattura selezionata.</DialogDescription>
          </DialogHeader>
          {editInvoice && (
            <div className="grid gap-4">
              <div>
                <Label>Numero *</Label>
                <Input data-testid="ei-number" value={editInvoice.number}
                  onChange={(e)=>setEditInvoice({...editInvoice, number:e.target.value})}/>
              </div>
              <div>
                <Label>Cliente (azienda) *</Label>
                <Select value={editInvoice.client_id} onValueChange={(v)=>setEditInvoice({...editInvoice, client_id:v})}>
                  <SelectTrigger data-testid="ei-client"><SelectValue placeholder="Seleziona un'azienda…"/></SelectTrigger>
                  <SelectContent>{clients.map(c=><SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name || "—"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Progetto</Label>
                <Select
                  value={editInvoice.project_id || "_none"}
                  onValueChange={(v)=>setEditInvoice({...editInvoice, project_id: v==="_none" ? "" : v})}
                >
                  <SelectTrigger data-testid="ei-project"><SelectValue placeholder="Nessuno"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nessuno</SelectItem>
                    {projects.filter(p=>!editInvoice.client_id || p.client_id===editInvoice.client_id).map(p=><SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importo (€) *</Label>
                <Input data-testid="ei-amount" type="number" step="0.01" value={editInvoice.amount}
                  onChange={(e)=>setEditInvoice({...editInvoice, amount: parseFloat(e.target.value) || 0})}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Emessa il</Label>
                  <Input type="date" data-testid="ei-issued" value={editInvoice.issued_date}
                    onChange={(e)=>setEditInvoice({...editInvoice, issued_date:e.target.value})}/>
                </div>
                <div>
                  <Label>Scadenza</Label>
                  <Input type="date" data-testid="ei-due" value={editInvoice.due_date}
                    onChange={(e)=>setEditInvoice({...editInvoice, due_date:e.target.value})}/>
                </div>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={editInvoice.status} onValueChange={(v)=>setEditInvoice({...editInvoice, status:v})}>
                  <SelectTrigger data-testid="ei-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bozza">Bozza</SelectItem>
                    <SelectItem value="inviata">Inviata</SelectItem>
                    <SelectItem value="pagata">Pagata</SelectItem>
                    <SelectItem value="scaduta">Scaduta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editInvoice.status === "pagata" && (
                <div>
                  <Label>Data pagamento</Label>
                  <Input type="date" data-testid="ei-paid-date" value={editInvoice.paid_date}
                    onChange={(e)=>setEditInvoice({...editInvoice, paid_date:e.target.value})}/>
                </div>
              )}
              <div>
                <Label>Note</Label>
                <Textarea data-testid="ei-notes" rows={2} value={editInvoice.notes}
                  onChange={(e)=>setEditInvoice({...editInvoice, notes:e.target.value})}/>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={()=>{ if (editInvoice && window.confirm("Eliminare questa fattura?")) { api.delete(`/invoices/${editInvoice.invoice_id}`).then(()=>{ toast.success("Fattura eliminata"); setEditOpen(false); load(); }); } }}
              data-testid="ei-delete"
              className="text-zen-alert hover:text-zen-alert/80 hover:bg-zen-alert/5 sm:mr-auto"
            >
              <Trash2 size={14} className="mr-1"/> Elimina
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={()=>setEditOpen(false)}>Annulla</Button>
              <Button data-testid="ei-save" onClick={saveEdit} disabled={saving} className="bg-zen-primary hover:bg-zen-primaryHover">
                {saving ? "Salvo…" : "Salva"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up dialog */}
      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sollecito pagamento AI</DialogTitle>
            <DialogDescription className="sr-only">Genera un sollecito con Claude e invialo via email o WhatsApp.</DialogDescription>
          </DialogHeader>
          {fuInvoice && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-zen-bg text-sm">
                <div>Fattura <b>{fuInvoice.number}</b> · {clientName(fuInvoice.client_id)}</div>
                <div className="text-zen-ink2 mt-0.5">{fmtEur2(fuInvoice.amount)} · scadenza {fmtDate(fuInvoice.due_date)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Canale</Label>
                  <div className="flex gap-2 mt-1.5">
                    {[{v:"email",i:Mail,l:"Email"},{v:"whatsapp",i:MessageCircle,l:"WhatsApp"}].map(x => (
                      <button key={x.v} data-testid={`fu-channel-${x.v}`} onClick={()=>setFuChannel(x.v)}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-sm transition-colors ${fuChannel===x.v ? "bg-zen-primary text-white" : "bg-white border border-zen-border text-zen-ink2"}`}>
                        <x.i size={14}/>{x.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Tono</Label>
                  <Select value={fuTone} onValueChange={setFuTone}>
                    <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cortese">Cortese</SelectItem>
                      <SelectItem value="amichevole">Amichevole</SelectItem>
                      <SelectItem value="fermo">Fermo ma professionale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="fu-generate" onClick={generateFu} disabled={fuLoading} variant="outline" className="w-full rounded-full">
                <Sparkles size={14} className="mr-1.5"/>{fuLoading ? "Genero…" : (fuMessage ? "Rigenera" : "Genera messaggio con Claude")}
              </Button>
              <Textarea
                data-testid="fu-message" rows={8}
                value={fuMessage}
                onChange={(e)=>setFuMessage(e.target.value)}
                placeholder={fuLoading ? "Sto scrivendo…" : "Clicca 'Genera' oppure scrivi qui il tuo messaggio."}
                className="font-mono text-sm"
              />
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={()=>setFuOpen(false)} className="sm:mr-auto">Chiudi</Button>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button data-testid="fu-copy" onClick={copyFu} disabled={!fuMessage} variant="outline" className="rounded-full">
                <Copy size={14} className="mr-1.5"/>Copia
              </Button>
              {fuChannel === "email" ? (
                <Button data-testid="fu-open-mail" onClick={openInMail} disabled={!fuMessage} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
                  <Mail size={14} className="mr-1.5"/>Apri in email <ExternalLink size={12} className="ml-1"/>
                </Button>
              ) : (
                <Button
                  data-testid="fu-open-wa"
                  onClick={openInWhatsApp}
                  disabled={!fuMessage}
                  className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
                >
                  <MessageCircle size={14} className="mr-1.5"/>Apri in WhatsApp <ExternalLink size={12} className="ml-1"/>
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* SdI readiness dialog — shown when required fiscal fields are missing */}
      <Dialog open={sdiWarnOpen} onOpenChange={setSdiWarnOpen}>
        <DialogContent className="max-w-md" data-testid="sdi-incomplete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileWarning size={18} className="text-zen-alert"/>
              {sdiMissingKind === "client"
                ? "Dati fiscali cliente incompleti"
                : "Dati aziendali incompleti"}
            </DialogTitle>
            <DialogDescription>
              {sdiMissingKind === "client" ? (
                <>Completa i dati fiscali di <b>{sdiMissingClientName || "questo cliente"}</b> prima di generare la fattura elettronica.</>
              ) : (
                <>Prima di generare la fattura elettronica, completa i dati aziendali obbligatori nella pagina <b>Impostazioni Azienda</b>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {sdiMissing.length > 0 && (
            <div className="rounded-xl bg-zen-alert/5 border border-zen-alert/30 p-3">
              <div className="text-sm font-medium text-zen-alert mb-1.5">Campi mancanti:</div>
              <ul className="text-sm text-zen-ink space-y-1 list-disc pl-5" data-testid="sdi-missing-list">
                {sdiMissing.map((m, idx) => <li key={idx}>{m}</li>)}
              </ul>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={()=>setSdiWarnOpen(false)} data-testid="sdi-warn-cancel">
              Annulla
            </Button>
            {sdiMissingKind === "client" ? (
              <Button
                onClick={goToClient}
                data-testid="sdi-go-to-client"
                className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
              >
                <SettingsIcon size={14} className="mr-1.5"/> Vai al cliente
              </Button>
            ) : (
              <Button
                onClick={goToSettings}
                data-testid="sdi-go-to-settings"
                className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
              >
                <SettingsIcon size={14} className="mr-1.5"/> Vai a Impostazioni Azienda
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
